import React, { useState } from 'react';
import _ from 'lodash';
import { IconButton } from '../../../components/common/IconButton';
import AIModelBadge from '../../../components/ai/AIModelBadge';
import { FactoidSpecs } from './FactoidSpecs';
import { Icon } from '../../../components/common/Icon';
import { FactoidImage } from './FactoidImage';
import { sortObjectKeysDeep } from '../../../../../model/cerokm/cerokmUtils.mjs';

function factoidToString(factoid) {
  let {derivedSpecs, missingInSchema, isObsolete} = factoid;
  if(derivedSpecs || missingInSchema || isObsolete) {
    if(_.isEmpty(derivedSpecs)) {
      derivedSpecs = null;
    }
    if(_.isEmpty(missingInSchema)) {
      missingInSchema = null;
    }
    return JSON.stringify([sortObjectKeysDeep(derivedSpecs), sortObjectKeysDeep(missingInSchema), isObsolete]);
  } else {
    // This case might need review depending on how 'factoid.derivedSpecs' is structured when neither derivedSpecs nor missingInSchema exist.
    // Returning null or an empty string might be safer if factoid.derivedSpecs could be undefined/null here.
    return JSON.stringify(factoid.derivedSpecs);
  }
}

export const FragmentFactoids = ({ fragment, onAcceptFactoids = null, onRejectFactoids = null, onCloneFactoid = null, specRegex = null }) => {
  const [showObsoleteFactoids, setShowObsoleteFactoids] = useState(false);

  const o = fragment; // Use 'o' for consistency with original code, or rename for clarity

  let obsoleteFactoids = _.filter(o.factoids, f => f.isObsolete);
  let validFactoids = showObsoleteFactoids ? o.factoids : _.filter(o.factoids, f => !f.isObsolete);

  let factoidsByDerivedSpecs = _.groupBy(validFactoids, factoidToString);

  let commonSpecs;
  let status = 'noFactoids';

  if (validFactoids.length) {
    if (_.some(validFactoids, f => f.manualRevision === 'correct')) {
      status = 'okRevision';
    } else if (_.every(validFactoids, f => f.isObsolete)) {
      status = 'obsolete';
    } else if (validFactoids.length > 1) {
      const commonSpecsPairs = _.intersectionBy(... _.map(factoidsByDerivedSpecs, group => group[0].derivedSpecs), pair => JSON.stringify(pair));
      commonSpecs = _.map(commonSpecsPairs, pair => pair[0]);

      if (_.keys(factoidsByDerivedSpecs).length > 1) {
        status = 'conflict';
      } else {
        // Check if the only group key corresponds to 'no useful data' (null/empty derivedSpecs and missingInSchema)
        const groupKey = _.keys(factoidsByDerivedSpecs)[0];
        try {
          const parsedKey = JSON.parse(groupKey);
          if (parsedKey && parsedKey[0] === null && parsedKey[1] === null) {
             status = 'noUsefulData';
          } else {
             status = 'okMultiple';
             commonSpecs = []; // Keep commonSpecs empty if only one non-empty group
          }
        } catch(e) {
           // Handle potential JSON parsing errors if needed
           console.error("Error parsing factoid group key:", groupKey, e);
           status = 'conflict'; // Default to conflict on error
        }

      }
    } else if (validFactoids[0].missingInSchema?.length) {
      status = 'conflict';
    } else if (validFactoids[0].derivedSpecs?.length) {
      status = 'okSingle';
    } else {
      status = 'noSpecs';
    }
  }

  let statusColors = {
    okMultiple: '#c3ffcb',
    okRevision: 'white',
    okSingle: '#efffd5',
    conflict: '#ffebc1',
    noUsefulData: '#bbb',
    noSpecs: '#ffbdbd',
    noFactoids: 'transparent',
    obsolete: '#AAA',
  };

  return (
    <div className={'border-left p-1 '+ (status === 'obsolete' ? 'translucent' : '')} style={{backgroundColor: statusColors[status]}} >
      {obsoleteFactoids.length ? <IconButton fill small className='mb-1 zoom-75 py-0' icon={showObsoleteFactoids ? 'visibility_off' : 'visibility'} level={'secondary'} onClick={() => setShowObsoleteFactoids(!showObsoleteFactoids)}>
        {obsoleteFactoids.length} obsolete factoids
      </IconButton> : null}

      {commonSpecs?.length ? <div className={'bg-white rounded p-1'}>
        All:
        <FactoidSpecs factoid={{derivedSpecs: _.filter(validFactoids[0].derivedSpecs, p => commonSpecs.includes(p[0]))}} specRegex={specRegex}/>
      </div> : null}

      {_.map(factoidsByDerivedSpecs, (fs, specs) => {
        let groupIsCorrect = _.some(fs, f => f.manualRevision === 'correct');
        let groupIsIncorrect = _.some(fs, f => f.manualRevision === 'incorrect');

        return <div key={specs} className={`py-1 ${groupIsIncorrect ? 'translucent bg-light-secondary' : (groupIsCorrect ? 'bg-light-success' : '')}`}
                    style={(groupIsCorrect ? {background: statusColors.okMultiple} : {})}
        >
          <div className={'d-flex justify-content-between'}>
            <div>
              <div>
              {
                _.map(_.sortBy(fs, 'schemaVersion'), (f, j) => <span key={f._id} className={'small mr-1'}>
                    <AIModelBadge model={f.derivedBy}/>

                  {fs[j + 1]?.schemaVersion !== f.schemaVersion ?
                    <span title={`Schema: ${f.schemaVersion}. Prompt: ${f.promptVersion}`}
                          className={'badge badge-secondary mr-3'}> {f.schemaVersion} </span> :
                    null}
                </span>
              )}
                {groupIsCorrect ? <Icon icon={'done_all'} level={'success'} title='Marked correct' /> : null}
                {groupIsIncorrect ? <Icon icon={'cancel'} level={'danger'} title='Marked incorrect' /> : null}
              </div>

              { fragment.type === 'image' ? <FactoidImage factoid={fs[0]}/> : <FactoidSpecs factoid={fs[0]} skipPaths={commonSpecs} specRegex={specRegex}/> }
            </div>

            <div className={`align-self-center zoom-75 mr-1 ${status !== 'conflict' ? 'parent-hover-transparent': ''}`}>
              { !groupIsCorrect && onAcceptFactoids ? <IconButton icon={'check'} level={'success'} className={'p-0'}
                          onClick={() => onAcceptFactoids(fs)}/> : null }
              <br/>
              { !groupIsIncorrect && onRejectFactoids ? <IconButton icon={'close'} level={'danger'} className={'p-0'}
                          onClick={() => onRejectFactoids(fs)}/> : null }
              <br/>
              { onCloneFactoid ? <IconButton icon={'content_copy'} level={'primary'} className={'p-0'}
                          onClick={() => onCloneFactoid(fs[0])}/> : null }
            </div>
          </div>
        </div>;
      })}
    </div>
  );
};
